import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ThumbsUp, MessageSquare, HandHelping } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError, CATEGORY_LABELS, STATUS, t } from "@/lib/api";
import { PageHeader } from "@/components/Common";

export const StatusBadge = ({ status }) => (
  <span data-testid="idea-status-badge" className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[status].cls}`}>{STATUS[status].label}</span>
);

function NewIdea({ open, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", category: "processus", impact: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/ideas", f); toast.success(t("Idée soumise ! +25 pts", "Idea submitted! +25 pts")); setF({ title: "", description: "", category: "processus", impact: "" }); onCreated(); onClose(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="idea-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("Nouvelle micro-idée", "New micro-idea")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2">
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <button type="button" key={k} data-testid={`idea-category-${k}`} onClick={() => setF({ ...f, category: k })}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${f.category === k ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 hover:border-indigo-300"}`}>{v}</button>
            ))}
          </div>
          <Input data-testid="idea-title-input" placeholder={t("Titre court et percutant", "Short, punchy title")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required minLength={3} />
          <Textarea data-testid="idea-description-input" rows={4} placeholder={t("Décrivez le problème et votre proposition (10 caractères min.)", "Describe the problem and your proposal (10 characters min.)")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required minLength={10} />
          <Input data-testid="idea-impact-input" placeholder={t("Impact attendu (optionnel)", "Expected impact (optional)")} value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })} />
          <button data-testid="idea-submit-button" className="w-full rounded-full bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 transition-colors">{t("Soumettre", "Submit")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function IdeasLab() {
  const [ideas, setIdeas] = useState([]);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/ideas", { params: { status: status || undefined, category: category || undefined, sort } }).then(({ data }) => setIdeas(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, category, sort]);

  const vote = async (e, id) => {
    e.preventDefault();
    try { const { data } = await api.post(`/ideas/${id}/vote`); setIdeas(ideas.map((i) => (i.idea_id === id ? data : i))); }
    catch (err) { toast.error(formatError(err)); }
  };

  const Chip = ({ value, current, set, label, testid }) => (
    <button data-testid={testid} onClick={() => set(value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${current === value ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>{label}</button>
  );

  return (
    <div>
      <PageHeader eyebrow={t("Incubateur", "Incubator")} title={t("Laboratoire d'idées", "Idea lab")} subtitle={t("Soumettez une micro-idée, enrichissez celles des autres, proposez une mission d'un jour. Chaque idée reçoit une réponse.", "Submit a micro-idea, build on others' ideas, offer a one-day mission. Every idea gets a response.")}>
        <button data-testid="idea-new-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 font-semibold text-slate-900 hover:bg-amber-500 transition-colors"><Plus className="h-4 w-4" /> {t("Proposer une idée", "Suggest an idea")}</button>
      </PageHeader>
      <div className="mb-8 flex flex-wrap items-center gap-2">
        <Chip value="" current={status} set={setStatus} label={t("Toutes", "All")} testid="idea-filter-status-all" />
        {Object.entries(STATUS).map(([k, v]) => <Chip key={k} value={k} current={status} set={setStatus} label={v.label} testid={`idea-filter-status-${k}`} />)}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        <Chip value="" current={category} set={setCategory} label={t("Tous thèmes", "All topics")} testid="idea-filter-cat-all" />
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => <Chip key={k} value={k} current={category} set={setCategory} label={v} testid={`idea-filter-cat-${k}`} />)}
        <select data-testid="idea-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold">
          <option value="recent">{t("Plus récentes", "Most recent")}</option><option value="votes">{t("Plus votées", "Most voted")}</option>
        </select>
      </div>
      <div className="grid gap-5 md:grid-cols-2" data-testid="ideas-list">
        {ideas.map((i, n) => (
          <Link key={i.idea_id} to={`/app/idees/${i.idea_id}`} data-testid={`idea-card-${i.idea_id}`} className="lift flex flex-col rounded-3xl border border-slate-200 bg-white p-6 fade-up" style={{ animationDelay: `${n * 0.04}s` }}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{CATEGORY_LABELS[i.category]}</span>
              <StatusBadge status={i.status} />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold leading-snug">{i.title}</h3>
            <p className="mt-2 text-sm text-slate-500 line-clamp-2">{i.description}</p>
            <div className="mt-auto pt-6 flex items-center gap-4 text-sm text-slate-500">
              <button data-testid={`idea-vote-button-${i.idea_id}`} onClick={(e) => vote(e, i.idea_id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition-colors ${i.has_voted ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}>
                <ThumbsUp className="h-3.5 w-3.5" /> {i.vote_count}
              </button>
              <span className="flex items-center gap-1"><MessageSquare className="h-4 w-4" />{i.comment_count}</span>
              <span className="flex items-center gap-1"><HandHelping className="h-4 w-4" />{i.mission_count}</span>
              <span className="ml-auto text-xs">{i.author_name}</span>
            </div>
          </Link>
        ))}
        {ideas.length === 0 && <p className="text-slate-500" data-testid="ideas-empty">{t("Aucune idée pour ces filtres. Lancez la première !", "No ideas match these filters. Be the first!")}</p>}
      </div>
      <NewIdea open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
